import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Receipt,
  Activity,
} from "lucide-react";
import { useDashboardData } from "../hooks/useDashboard";
import toast from "react-hot-toast";

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: number;
  trendLabel?: string;
  color: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  trend,
  trendLabel,
  color,
}) => {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 hover:border-slate-600 transition-all">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${color}`}>{icon}</div>
        {trend !== undefined && (
          <div
            className={`flex items-center text-sm ${trend >= 0 ? "text-green-400" : "text-red-400"}`}
          >
            {trend >= 0 ? (
              <TrendingUp className="w-4 h-4 mr-1" />
            ) : (
              <TrendingDown className="w-4 h-4 mr-1" />
            )}
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <h3 className="text-slate-400 text-sm font-medium mb-1">{title}</h3>
      <p className="text-2xl font-bold text-white">{value}</p>
      {trendLabel && (
        <p className="text-xs text-slate-500 mt-2">{trendLabel}</p>
      )}
    </div>
  );
};

const Dashboard: React.FC = () => {
  const [dateRange, setDateRange] = useState<
    "today" | "week" | "month" | "year"
  >("week");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [customDateRange, setCustomDateRange] = useState(false);
  const [selectedLines, setSelectedLines] = useState({
    cashIn: true,
    cashOut: true,
    expenses: true,
  });

  const [selectedBars, setSelectedBars] = useState({
    cashIn: true,
    cashOut: true,
  });

  const {
    data: dashboardData,
    isLoading,
    error,
  } = useDashboardData({
    startDate,
    endDate,
    range: customDateRange ? undefined : dateRange,
  });

  useEffect(() => {
    if (!customDateRange) {
      const today = new Date();
      let start: Date;
      const end = today;

      switch (dateRange) {
        case "today":
          start = new Date(today);
          break;
        case "week":
          start = new Date(today);
          start.setDate(today.getDate() - 7);
          break;
        case "month":
          start = new Date(today);
          start.setMonth(today.getMonth() - 1);
          break;
        case "year":
          start = new Date(today);
          start.setFullYear(today.getFullYear() - 1);
          break;
        default:
          start = new Date(today);
          start.setDate(today.getDate() - 7);
      }

      setStartDate(start.toISOString().split("T")[0]);
      setEndDate(end.toISOString().split("T")[0]);
    }
  }, [dateRange, customDateRange]);

  const handleCustomDateChange = () => {
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        toast.error("Start date must be before end date");
        return;
      }
      setCustomDateRange(true);
    }
  };

  const COLORS = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
  ];

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleLegendClick = (dataKey: string) => {
    setSelectedLines((prev) => ({
      ...prev,
      [dataKey]: !prev[dataKey as keyof typeof prev],
    }));
  };

  const handleBarLegendClick = (dataKey: string) => {
    setSelectedBars((prev) => ({
      ...prev,
      [dataKey]: !prev[dataKey as keyof typeof prev],
    }));
  };

  if (error) {
    return (
      <div className="p-6 bg-slate-900 min-h-screen">
        <div className="bg-red-500/10 border border-red-500 text-red-400 p-4 rounded-lg">
          Error loading dashboard data. Please try again later.
        </div>
      </div>
    );
  }

  const mockData = {
    metrics: {
      cashIn: 2456789,
      cashOut: 1234567,
      expenses: 345678,
      dailyEntries: 127,
      activeCustomers: 89,
      totalRevenue: 987654,
      cashInTrend: 12.5,
      cashOutTrend: -5.3,
      expensesTrend: 8.2,
      entriesTrend: 15.0,
    },
    chartData: [
      { date: "Mon", cashIn: 320000, cashOut: 180000, expenses: 45000 },
      { date: "Tue", cashIn: 280000, cashOut: 200000, expenses: 38000 },
      { date: "Wed", cashIn: 350000, cashOut: 160000, expenses: 52000 },
      { date: "Thu", cashIn: 420000, cashOut: 240000, expenses: 48000 },
      { date: "Fri", cashIn: 380000, cashOut: 190000, expenses: 41000 },
      { date: "Sat", cashIn: 450000, cashOut: 280000, expenses: 58000 },
      { date: "Sun", cashIn: 256789, cashOut: 186567, expenses: 63678 },
    ],
    expenseBreakdown: [
      { name: "Salaries", value: 120000 },
      { name: "Utilities", value: 45000 },
      { name: "Maintenance", value: 38000 },
      { name: "Marketing", value: 62000 },
      { name: "Operations", value: 80678 },
    ],
  };

  const data = dashboardData || mockData;

  return (
    <div className="p-6 bg-slate-900 min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-slate-400">Overview of your casino operations</p>
      </div>

      <div className="mb-6 bg-slate-800 border border-slate-700 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-400" />
            <span className="text-slate-300 font-medium">Date Range:</span>
          </div>

          <div className="flex flex-wrap gap-2">
            {(["today", "week", "month", "year"] as const).map((range) => (
              <button
                key={range}
                onClick={() => {
                  setDateRange(range);
                  setCustomDateRange(false);
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  !customDateRange && dateRange === range
                    ? "bg-blue-600 text-white"
                    : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                }`}
              >
                {range === "today"
                  ? "Today"
                  : range === "week"
                    ? "This Week"
                    : range === "month"
                      ? "This Month"
                      : "This Year"}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <input
              type="date"
              value={customDateRange ? startDate : ""}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={customDateRange ? endDate : ""}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
            />
            <button
              onClick={handleCustomDateChange}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Cash In"
          value={formatCurrency(data.metrics.cashIn)}
          icon={<DollarSign className="w-6 h-6 text-white" />}
          trend={data.metrics.cashInTrend}
          trendLabel="vs last period"
          color="bg-green-600"
        />
        <MetricCard
          title="Cash Out"
          value={formatCurrency(data.metrics.cashOut)}
          icon={<DollarSign className="w-6 h-6 text-white" />}
          trend={data.metrics.cashOutTrend}
          trendLabel="vs last period"
          color="bg-red-600"
        />
        <MetricCard
          title="Expenses"
          value={formatCurrency(data.metrics.expenses)}
          icon={<Receipt className="w-6 h-6 text-white" />}
          trend={data.metrics.expensesTrend}
          trendLabel="vs last period"
          color="bg-orange-600"
        />
        <MetricCard
          title="Daily Entries"
          value={data.metrics.dailyEntries}
          icon={<Activity className="w-6 h-6 text-white" />}
          trend={data.metrics.entriesTrend}
          trendLabel="vs last period"
          color="bg-blue-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4">Cash Flow Trend</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9CA3AF" />
              <YAxis
                stroke="#9CA3AF"
                tickFormatter={(value) => `₹${value / 1000}k`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1F2937",
                  border: "1px solid #374151",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "#9CA3AF" }}
                itemStyle={{ color: "#fff" }}
                formatter={(value: number) => formatCurrency(value)}
              />
              <Legend
                onClick={(data) =>
                  typeof data.dataKey === "string" &&
                  handleLegendClick(data.dataKey)
                }
                wrapperStyle={{ cursor: "pointer" }}
              />
              <Line
                type="monotone"
                dataKey="cashIn"
                stroke="#10b981"
                strokeWidth={2}
                name="Cash In"
                hide={!selectedLines.cashIn}
              />
              <Line
                type="monotone"
                dataKey="cashOut"
                stroke="#ef4444"
                strokeWidth={2}
                name="Cash Out"
                hide={!selectedLines.cashOut}
              />
              <Line
                type="monotone"
                dataKey="expenses"
                stroke="#f59e0b"
                strokeWidth={2}
                name="Expenses"
                hide={!selectedLines.expenses}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4">
            Daily Comparison
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9CA3AF" />
              <YAxis
                stroke="#9CA3AF"
                tickFormatter={(value) => `₹${value / 1000}k`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1F2937",
                  border: "1px solid #374151",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "#9CA3AF" }}
                itemStyle={{ color: "#fff" }}
                formatter={(value: number) => formatCurrency(value)}
              />
              <Legend
                onClick={(data) => {
                  if (data?.dataKey) {
                    handleBarLegendClick(String(data.dataKey));
                  }
                }}
                wrapperStyle={{ cursor: "pointer" }}
              />
              <Bar
                dataKey="cashIn"
                fill="#10b981"
                name="Cash In"
                hide={!selectedBars.cashIn}
              />
              <Bar
                dataKey="cashOut"
                fill="#ef4444"
                name="Cash Out"
                hide={!selectedBars.cashOut}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4">Net Cash Flow</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9CA3AF" />
              <YAxis
                stroke="#9CA3AF"
                tickFormatter={(value) => `₹${value / 1000}k`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1F2937",
                  border: "1px solid #374151",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "#9CA3AF" }}
                itemStyle={{ color: "#fff" }}
                formatter={(value: number) => formatCurrency(value)}
              />
              <Bar
                dataKey={(d: {
                  cashIn: number;
                  cashOut: number;
                  expenses: number;
                }) => d.cashIn - d.cashOut - d.expenses}
                fill="#3b82f6"
                name="Net Cash Flow"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4">
            Expense Breakdown
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={
                  data.expenseBreakdown as unknown as Record<string, unknown>[]
                }
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry: { name?: string; value?: number }) => {
                  if (!entry.name || !entry.value) return "";
                  const percentage =
                    data.metrics.expenses > 0
                      ? ((entry.value / data.metrics.expenses) * 100).toFixed(1)
                      : "0";
                  return `${entry.name}: ${percentage}%`;
                }}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.expenseBreakdown.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1F2937",
                  border: "1px solid #374151",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "#9CA3AF" }}
                itemStyle={{ color: "#fff" }}
                formatter={(value: number) => formatCurrency(value)}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Legend for expense breakdown */}
          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
            {data.expenseBreakdown.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded"
                  style={{
                    backgroundColor: COLORS[index % COLORS.length],
                  }}
                />
                <span className="text-slate-300 truncate">
                  {item.name}: {formatCurrency(item.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="flex items-center gap-3 bg-slate-800 px-6 py-4 rounded-lg border border-slate-700">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-white">Loading dashboard data...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
